import { RiArrowDownSLine, RiSearchLine } from 'react-icons/ri';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type JSX,
} from 'react';
import { createPortal } from 'react-dom';
import type {
  BrandScopeDropdownProps,
  BrandScopeTriggerRect,
} from '../BrandOffersPage.types';
import { BRAND_SCOPE_DROPDOWN_COPY } from './BrandOfferForm.constants';
import styles from './BrandOfferForm.module.css';

const BrandScopeDropdown = ({
  brands,
  value,
  onChange,
}: BrandScopeDropdownProps): JSX.Element => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [rect, setRect] = useState<BrandScopeTriggerRect | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const updateRect = useCallback((): void => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const edge = 12;
    const width = Math.min(r.width, window.innerWidth - edge * 2);
    const left = Math.min(
      Math.max(edge, r.left),
      window.innerWidth - width - edge
    );
    const maxTop = window.innerHeight - edge;
    const top = Math.min(r.bottom + 4, maxTop - 48);
    setRect({ top, left, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      setSearch('');
      return;
    }
    updateRect();
  }, [open, updateRect]);

  useEffect(() => {
    if (!open) return;
    const id = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onReposition = (): void => updateRect();
    const onMouseDown = (e: MouseEvent): void => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || menuRef.current?.contains(t)) {
        return;
      }
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };

    window.addEventListener('resize', onReposition);
    document.addEventListener('scroll', onReposition, true);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('resize', onReposition);
      document.removeEventListener('scroll', onReposition, true);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateRect]);

  const selectedBrand =
    value == null ? null : brands.find((b) => b.id === value);
  const selectedLabel =
    value == null
      ? BRAND_SCOPE_DROPDOWN_COPY.placeholder
      : (selectedBrand?.name ?? `Brand #${value}`);

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.name.toLowerCase().includes(q));
  }, [brands, search]);

  const handleSelect = useCallback(
    (id: number | null): void => {
      onChange(id);
      setOpen(false);
    },
    [onChange]
  );

  const optionClass = (active: boolean): string =>
    active
      ? `${styles.brandDropdownOption} ${styles.brandDropdownOptionActive}`
      : styles.brandDropdownOption;

  const menu =
    open &&
    rect &&
    createPortal(
      <div
        ref={menuRef}
        className={styles.brandDropdownPortal}
        style={{
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          zIndex: 250,
        }}
      >
        <div className={styles.brandSearchWrap}>
          <RiSearchLine
            aria-hidden
            size={14}
            className={styles.brandSearchIcon}
          />
          <input
            ref={searchRef}
            type="search"
            className={styles.brandSearchInput}
            placeholder={BRAND_SCOPE_DROPDOWN_COPY.searchPlaceholder}
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setSearch(e.target.value)
            }
          />
        </div>
        <ul
          className={styles.brandDropdownList}
          role="listbox"
          aria-label={BRAND_SCOPE_DROPDOWN_COPY.listboxAria}
        >
          {filteredBrands.length === 0 ? (
            <li className={styles.brandDropdownEmpty}>
              {BRAND_SCOPE_DROPDOWN_COPY.empty}
            </li>
          ) : (
            filteredBrands.map((b) => (
              <li key={b.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={value === b.id}
                  className={optionClass(value === b.id)}
                  onClick={() => handleSelect(b.id)}
                >
                  {b.logo ? (
                    <img
                      src={b.logo}
                      alt=""
                      className={styles.brandLogo}
                      loading="lazy"
                    />
                  ) : (
                    <span aria-hidden className={styles.brandLogoFallback}>
                      {b.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                  <span className={styles.brandOptionLabel}>{b.name}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </div>,
      document.body
    );

  return (
    <div className={styles.brandDropdown}>
      <button
        ref={triggerRef}
        type="button"
        id="offer-brand"
        className={`${styles.brandDropdownTrigger} ${open ? styles.brandDropdownTriggerOpen : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.brandTriggerInner}>
          {selectedBrand?.logo ? (
            <img
              src={selectedBrand.logo}
              alt=""
              className={styles.brandLogo}
              loading="lazy"
            />
          ) : null}
          <span>{selectedLabel}</span>
        </span>
        <RiArrowDownSLine
          aria-hidden
          size={20}
          className={`${styles.brandChevron} ${open ? styles.brandChevronOpen : ''}`}
        />
      </button>
      {menu}
    </div>
  );
};

export default BrandScopeDropdown;
